import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import classes from '../../../pipeline.module.scss';
import Api from '../../../../../../../api/api';
import TableAndPagination from '../../../../../../_common/TableAndPagination/TableAndPagination';
import Modal from '../../../../../../_common/Modal/Modal';
import Loader from '../../../../../../_common/Loader/Loader';
import StatusField from '../../../../../../_common/StatusField/StatusField';
import Chip from '../../../../../../_common/Chip/Chip';
import { formattedCount, formattedDate } from '../../../pipelineHelpers';

const tableHeader = [
  { name: 'Date', path: 'date' },
  { name: 'Member ID', path: 'memberId' },
  { name: 'Member Name', path: 'memberName' },
  { name: 'DOB', path: 'dob' },
  { name: 'LOB', path: 'lob' },
  { name: 'Source', path: 'source' },
  {
    name: 'Status',
    path: 'status',
    render: (d) => (d.status ? <StatusField status={d.status} /> : '-'),
  },
  { name: 'Action Needed By', path: 'actionNeededBy' },
  { name: 'Follow-up Date', path: 'followUpDate' },
  {
    name: 'Notes',
    path: 'notes',
    render: (d) => (d.notes
      ? (
        <div className={classes.details_notes_cell} title={d.notes}>
          {d.notes}
        </div>
      )
      : '-'),
  },
  {
    name: 'Final Outcome',
    path: 'finalOutcome',
    render: (d) => (d.finalOutcome
      ? (
        <div className={classes.details_chip_wrapper}>
          <Chip text={_.startCase(d.finalOutcome)} gray />
        </div>
      )
      : '-'),
  },
  { name: 'Final Outcome Date', path: 'finalOutcomeDate' },
];

const initialState = {
  firstLoad: true,
  loading: false,
  list: [],
  total: 0,
  totalPage: 1,
  page: 1,
  limit: 15,
};

const formattedDetails = (list) => (list || []).map((d) => ({
  ...d,
  date: formattedDate(d.date),
  dob: formattedDate(d.dob),
  followUpDate: formattedDate(d.followUpDate),
  finalOutcomeDate: formattedDate(d.finalOutcomeDate),
}));

const filtersCaption = (filters) => Object.values(filters).join(' / ') || 'Total Result';

const SummaryDetailsModal = ({
  isOpen, params, filters, onClose,
}) => {
  const [details, setDetails] = useState(initialState);

  const { page, limit } = details;

  useEffect(() => {
    if (!isOpen) setDetails(initialState);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      try {
        setDetails((prev) => ({ ...prev, loading: true }));

        const { data } = await Api.getIehpSummaryDetails({
          ...params, ...filters, page, limit,
        });

        setDetails((prev) => ({
          ...prev,
          list: formattedDetails(data?.data?.response),
          total: data?.data?._meta?.total || 0,
          totalPage: data?.data?._meta?.pageCount || 1,
        }));

        setDetails((prev) => ({ ...prev, loading: false, firstLoad: false }));
      } catch (e) {
        console.warn(e);
      }
    })();
  }, [isOpen, page, limit]);

  const onChangePageData = (path, value) => {
    setDetails((prev) => (path === 'limit'
      ? { ...prev, limit: value, page: 1 }
      : { ...prev, page: value }));
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className={`${classes.members_modal} ${classes.details_modal}`}
    >
      <div className={classes.members_modal_content}>
        <h2>Details</h2>

        {details.firstLoad
          ? (
            <div className={classes.members_modal_loader}>
              <Loader />
            </div>
          )

          : (
            <>
              <div className={classes.details_modal_caption}>
                {filtersCaption(filters)}

                {!!details.total && (
                  <span>{`${formattedCount(details.total)} records`}</span>
                )}
              </div>

              <div className={classes.members_modal_table}>
                <TableAndPagination
                  header={tableHeader}
                  data={details.list}
                  loading={details.loading}
                  onChange={onChangePageData}
                  totalPage={details.totalPage}
                  page={details.page - 1}
                  limit={details.limit}
                />
              </div>
            </>
          )}
      </div>
    </Modal>
  );
};

SummaryDetailsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  params: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default SummaryDetailsModal;
